import { invalidateQueryCache } from './queryCache';

/** One cookie-authenticated stream per signed-in browser tab. */
export function startTransactionLive() {
  let socket: WebSocket | undefined;
  let stopped = false;
  let connected = false;
  let attempt = 0;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let refresh: ReturnType<typeof setTimeout> | undefined;

  const scheduleRefresh = () => {
    // Batch import bursts without delaying an update indefinitely.
    if (refresh) return;
    refresh = setTimeout(() => {
      refresh = undefined;
      invalidateQueryCache();
    }, 150);
  };
  const connect = () => {
    if (stopped) return;
    const url = new URL(
      `${(import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '')}/transactions/live/`,
      window.location.origin
    );
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    try {
      socket = new WebSocket(url);
    } catch {
      reconnect();
      return;
    }
    const current = socket;
    const armWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        connected = false;
        current.close();
      }, 60_000);
    };
    armWatchdog();
    current.onmessage = event => {
      if (stopped || socket !== current) return;
      let type: string;
      try {
        type = JSON.parse(event.data).type;
      } catch {
        return;
      }
      if (!['ready', 'heartbeat', 'transactions.changed'].includes(type))
        return;
      armWatchdog();
      if (type === 'ready') {
        connected = true;
        attempt = 0;
        // Catch changes missed during a disconnect or before LISTEN was ready.
        scheduleRefresh();
      } else if (type === 'transactions.changed') scheduleRefresh();
    };
    current.onclose = () => {
      if (socket !== current) return;
      connected = false;
      clearTimeout(watchdog);
      reconnect();
    };
    current.onerror = () => current.close();
  };
  const reconnect = () => {
    if (stopped) return;
    const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt++, 5));
    retry = setTimeout(connect, delay + Math.random() * 500);
  };
  connect();
  return {
    get connected() {
      return connected;
    },
    stop() {
      stopped = true;
      connected = false;
      clearTimeout(retry);
      clearTimeout(watchdog);
      clearTimeout(refresh);
      socket?.close();
    },
  };
}
